/**
 * Lunar screen — moon calendar wired to the PUBLIC /lunar API.
 *
 * Surfaces two unauthenticated endpoints (services/api/.../routers/lunar.py):
 *   - GET /lunar/today?tz=<iana>          → current phase + sign + void-of-course
 *   - GET /lunar/upcoming?count=5&tz=<iana> → next notable events
 *
 * No birth data / session needed (both routes are public), so — unlike Today —
 * this page renders for everyone, anchored on the device timezone. On error we
 * show a dignified banner, never fabricated phase data (data-correctness rule).
 */
import { useQuery } from "@tanstack/react-query";
import type { ReactElement } from "react";

import { LunarEventRow } from "@/components/LunarEventRow";
import { MoonPhasePanel } from "@/components/MoonPhasePanel";
import { TabBar } from "@/components/TabBar";
import {
  fetchLunarToday,
  fetchLunarUpcoming,
  type LunarToday,
  type LunarUpcoming,
  resolveTimezone,
} from "@/lib/lunar-api";

export default function LunarPage(): ReactElement {
  const tz = resolveTimezone();

  const today = useQuery<LunarToday>({
    queryKey: ["lunar-today", tz],
    queryFn: () => fetchLunarToday(tz),
    staleTime: 30 * 60_000,
  });

  const upcoming = useQuery<LunarUpcoming>({
    queryKey: ["lunar-upcoming", tz],
    queryFn: () => fetchLunarUpcoming(tz, 5),
    staleTime: 30 * 60_000,
  });

  return (
    <main className="screen starry">
      <div className="body-pad has-tabbar">
        <header className="today-head">
          <div className="today-greet">
            <div className="day">Лунный календарь</div>
            <div className="name">Фаза Луны сегодня</div>
          </div>
        </header>

        {today.isLoading ? <SkeletonBody /> : null}
        {today.error ? <ErrorBanner message={(today.error as Error).message} /> : null}
        {today.data ? <MoonPhasePanel data={today.data} /> : null}

        <h2 className="section-h">
          <span>Ближайшие события</span>
        </h2>

        {upcoming.isLoading ? (
          <div
            role="status"
            aria-busy="true"
            aria-label="Загрузка событий"
            style={{ opacity: 0.4 }}
          >
            <div className="transit-row" style={{ minHeight: 56 }} />
            <div className="transit-row" style={{ minHeight: 56 }} />
          </div>
        ) : null}
        {upcoming.error ? <ErrorBanner message={(upcoming.error as Error).message} /> : null}
        {upcoming.data
          ? upcoming.data.events.map((e) => (
              <LunarEventRow
                key={`${e.kind}-${e.moment}`}
                kind={e.kind}
                moment={e.moment}
                sign={e.sign}
              />
            ))
          : null}
      </div>
      <TabBar active="today" />
    </main>
  );
}

function SkeletonBody(): ReactElement {
  return (
    <div role="status" aria-busy="true" aria-label="Загрузка фазы Луны" style={{ opacity: 0.4 }}>
      <div className="moon-card" style={{ minHeight: 120 }} />
    </div>
  );
}

function ErrorBanner({ message }: { readonly message: string }): ReactElement {
  return (
    <div className="ob-error" role="alert">
      Не получилось загрузить лунные данные: {message}
    </div>
  );
}
